import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Stateful Table Write Step
 *
 * Terminal step. Ingests a CSV payload into a versioned, schema-applied
 * stateful table. The active version's schema is applied during ingestion;
 * lookups by key column become available immediately afterwards.
 *
 * Modes:
 *   - "snapshot": the payload replaces the entire cumulative state of the
 *     table. Rows not in the payload are removed.
 *   - "upsert": the payload is merged into the cumulative state by the
 *     table's key column. New keys are inserted; existing keys are updated;
 *     rows not in the payload are preserved.
 */
export const statefulTableWriteStep = defineStepConfig({
  type: "stateful_table_write",
  name: "Stateful Table Write",
  description:
    "Ingest a CSV payload into a stateful table. In snapshot mode the payload replaces the entire table; in upsert mode the payload merges into the table by its key column.",
  category: "Data Access",

  inputSchema: z.object({
    rawData: z
      .string()
      .describe(
        "CSV string with a header row. Column headers must match the stateful table schema (case-insensitive).",
      ),
  }),

  outputSchema: z.object({}),

  configSchema: z.object({
    tableDefinitionId: z
      .string()
      .uuid()
      .describe("ID of the stateful table to write into"),
    writeMode: z
      .enum(["snapshot", "upsert"])
      .default("snapshot")
      .describe(
        "snapshot = replace the entire table with this payload. upsert = merge this payload into the table by the key column, preserving prior rows that aren't in the payload.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
